import { FC, useMemo, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Chip, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { ScalarLineChart } from 'components/Charts';
import {
  useGetCompanyZanbatoAskPricing,
  useGetCompanyZanbatoBidPricing,
  useGetCompanyZanbatoClosedTransactions,
  useGetCompanyZanbatoPricing,
  useGetCompanyZanbatoReportedMarks,
} from 'services/hooks/companies';
import {
  CardBody,
  CardTitle,
  CardTitleRow,
  OutlinedCard,
  SourceChip,
} from '../CompanyOverview.styles';
import {
  ChangeLabel,
  CurrentPrice,
  HeaderLeft,
  PeriodControls,
  RobustnessRow,
  SeriesToggleRow,
  StatBar,
  StatItem,
  StatLabel,
  StatValue,
  Subtitle,
} from './ZXIndexValueCard.styles';
import {
  buildAllSeries,
  buildTooltipExtras,
  buildTooltipFormatter,
  computeHeaderStats,
  DEFAULT_ENABLED_IDS,
  filterByPeriod,
  formatCurrency,
  formatPricingDate,
  formatVolume,
  getAsOfRecord,
  getPeriodStartDate,
  getRobustnessInfo,
  Period,
  PERIODS,
  PeriodValue,
  SERIES_CONFIGS,
} from './ZXIndexValueCard.utils';

interface Props {
  companyId: number | undefined;
  measurementDate?: string;
}

const ZXIndexValueCard: FC<Props> = ({ companyId, measurementDate }) => {
  const [period, setPeriod] = useState<PeriodValue>(Period.Y1);
  const [enabledIds, setEnabledIds] = useState<Set<string>>(DEFAULT_ENABLED_IDS);

  const { data: pricingData, isLoading: pricingLoading } = useGetCompanyZanbatoPricing(companyId);
  const { data: askData, isLoading: askLoading } = useGetCompanyZanbatoAskPricing(companyId);
  const { data: bidData, isLoading: bidLoading } = useGetCompanyZanbatoBidPricing(companyId);
  const { data: marksData, isLoading: marksLoading } = useGetCompanyZanbatoReportedMarks(companyId);
  const { data: txData, isLoading: txLoading } = useGetCompanyZanbatoClosedTransactions(companyId);

  const isLoading = pricingLoading || askLoading || bidLoading || marksLoading || txLoading;

  const allPricing = useMemo(() => pricingData?.pricing ?? [], [pricingData?.pricing]);
  const allAsk = useMemo(() => askData?.pricing ?? [], [askData?.pricing]);
  const allBid = useMemo(() => bidData?.pricing ?? [], [bidData?.pricing]);
  const allMarks = useMemo(() => marksData?.marks ?? [], [marksData?.marks]);
  const allTransactions = useMemo(() => txData?.transactions ?? [], [txData?.transactions]);

  const latestDate = useMemo(() => {
    if (!allPricing.length) {
      return undefined;
    }
    return [...allPricing]
      .filter(i => i.report_period)
      .sort((a, b) => b.report_period!.localeCompare(a.report_period!))
      [0]?.report_period ?? undefined;
  }, [allPricing]);

  const effectiveEndDate = measurementDate ?? latestDate;

  const filteredPricing = useMemo(() => {
    if (!effectiveEndDate || !allPricing.length) {
      return allPricing;
    }
    return filterByPeriod(allPricing, period, effectiveEndDate);
  }, [allPricing, period, effectiveEndDate]);

  const filteredAsk = useMemo(() => {
    if (!effectiveEndDate || !allAsk.length) {
      return allAsk;
    }
    return filterByPeriod(allAsk, period, effectiveEndDate);
  }, [allAsk, period, effectiveEndDate]);

  const filteredBid = useMemo(() => {
    if (!effectiveEndDate || !allBid.length) {
      return allBid;
    }
    return filterByPeriod(allBid, period, effectiveEndDate);
  }, [allBid, period, effectiveEndDate]);

  const filteredMarks = useMemo(() => {
    if (!effectiveEndDate || !allMarks.length) {
      return allMarks;
    }
    return filterByPeriod(allMarks, period, effectiveEndDate);
  }, [allMarks, period, effectiveEndDate]);

  // Transactions use closing_date rather than report_period so filter inline
  const filteredTransactions = useMemo(() => {
    if (!allTransactions.length) {
      return allTransactions;
    }
    if (period === Period.All) {
      return allTransactions.filter(t => t.closing_date);
    }
    if (!effectiveEndDate) {
      return allTransactions;
    }
    const startDate = getPeriodStartDate(effectiveEndDate, period);
    return allTransactions.filter(t => {
      if (!t.closing_date) {
        return false;
      }
      if (t.closing_date > effectiveEndDate) {
        return false;
      }
      if (startDate && t.closing_date < startDate) {
        return false;
      }
      return true;
    });
  }, [allTransactions, period, effectiveEndDate]);

  const series = useMemo(
    () => buildAllSeries(enabledIds, filteredPricing, filteredAsk, filteredBid, filteredMarks, filteredTransactions),
    [enabledIds, filteredPricing, filteredAsk, filteredBid, filteredMarks, filteredTransactions],
  );

  const tooltipFormatter = useMemo(() => {
    const extras = buildTooltipExtras(allPricing, allAsk, allBid, allMarks, allTransactions);
    return buildTooltipFormatter(extras);
  }, [allPricing, allAsk, allBid, allMarks, allTransactions]);

  const asOfRecord = useMemo(
    () => getAsOfRecord(allPricing, measurementDate),
    [allPricing, measurementDate],
  );

  const { currentValue, change } = useMemo(
    () => computeHeaderStats(filteredPricing, measurementDate),
    [filteredPricing, measurementDate],
  );

  const toggleSeries = (id: string) => {
    setEnabledIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const robustness = getRobustnessInfo(asOfRecord?.robustness);
  const hasData = allPricing.length > 0 || allAsk.length > 0 || allBid.length > 0 || allMarks.length > 0 || allTransactions.length > 0;

  return (
    <OutlinedCard variant="outlined">
      <CardBody>
        <CardTitleRow>
          <HeaderLeft>
            <CardTitle component="span">ZX Index Value</CardTitle>
            {hasData && (
              <>
                <CurrentPrice>{formatCurrency(currentValue)}</CurrentPrice>
                {change && (
                  <ChangeLabel positive={change.abs >= 0}>
                    {change.abs >= 0 ? '+' : ''}{change.abs.toFixed(2)}{' '}
                    ({change.abs >= 0 ? '+' : ''}{change.pct.toFixed(2)}%)
                  </ChangeLabel>
                )}
              </>
            )}
          </HeaderLeft>

          {hasData && (
            <PeriodControls>
              <ToggleButtonGroup
                value={period}
                exclusive
                size="small"
                onChange={(_e, v: PeriodValue | null) => {
                  if (v) {
                    setPeriod(v);
                  }
                }}
              >
                {PERIODS.map(p => (
                  <ToggleButton key={p} value={p}>{p}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
                href="https://zanbato.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', height: 26, py: 0 }}
              >
                Zanbato
              </Button>
            </PeriodControls>
          )}

          {!hasData && <SourceChip label="Zanbato" size="small" chipColor="blue" />}
        </CardTitleRow>

        {hasData && (
          <Subtitle>
            <span>ZX Index Value (Zanbato){effectiveEndDate ? ` as of ${effectiveEndDate}` : ''}</span>
            {robustness && (
              <>
                <Chip
                  label={robustness.label}
                  size="small"
                  color={robustness.color}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
                />
                <Tooltip title={`Robustness Score: ${robustness.confidence}`} arrow>
                  <InfoOutlinedIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              </>
            )}
          </Subtitle>
        )}

        {isLoading && (
          <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
        )}

        {!isLoading && !hasData && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            No Zanbato pricing data available for this company.
          </Typography>
        )}

        {!isLoading && hasData && (
          <>
            <SeriesToggleRow>
              {SERIES_CONFIGS.map(s => (
                <Chip
                  key={s.id}
                  label={s.label}
                  size="small"
                  onClick={() => toggleSeries(s.id)}
                  variant={enabledIds.has(s.id) ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: enabledIds.has(s.id) ? s.color : 'transparent',
                    borderColor: s.color,
                    color: enabledIds.has(s.id) ? '#fff' : s.color,
                    '&:hover': {
                      backgroundColor: enabledIds.has(s.id) ? s.color : `${s.color}22`,
                    },
                  }}
                />
              ))}
            </SeriesToggleRow>

            <ScalarLineChart
              series={series}
              notMerge
              smooth
              showPoints={false}
              style={{ height: 280 }}
              config={{
                legend: { show: false },
                xAxis: { formatter: v => formatPricingDate(String(v)) },
                yAxis: { formatter: v => `$${v}` },
                options: {
                  tooltip: {
                    trigger: 'axis',
                    appendToBody: true,
                    formatter: tooltipFormatter,
                  },
                },
              }}
            />

            <StatBar>
              <StatItem>
                <StatLabel>ZX Index Value (Current)</StatLabel>
                <StatValue>{formatCurrency(currentValue)}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Volume</StatLabel>
                <StatValue>{formatVolume(asOfRecord?.total_volume)}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Robustness Score</StatLabel>
                <StatValue>
                  {robustness ? (
                    <RobustnessRow>
                      <Chip
                        label={robustness.label}
                        size="small"
                        color={robustness.color}
                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
                      />
                      <span style={{ fontSize: '0.82rem', fontWeight: 400 }}>{robustness.confidence}</span>
                    </RobustnessRow>
                  ) : '—'}
                </StatValue>
              </StatItem>
            </StatBar>
          </>
        )}
      </CardBody>
    </OutlinedCard>
  );
};

export { ZXIndexValueCard };
